<template>
  <v-dialog
    v-model="showDialog"
    width="400px"
    persistent
  >
    <v-card>
      <v-toolbar
        color="primaryLight"
        dark
        dense
      >
        <v-toolbar-title>{{ isEdit ? 'Editar' : 'Crear' }} plan de operaciones</v-toolbar-title>
      </v-toolbar>

      <v-card-text
        class="pt-4"
      >
        <v-form
          v-model="isValid"
        >
          <v-text-field
            id="nombre"
            v-model="nombre"
            name="nombre"
            label="Nombre*"
            outlined
            :rules="[globalRules.required]"
          />
          <v-textarea
            v-model="descripcion"
            label="Descripción*"
            outlined
            :rules="[globalRules.required]"
          />

          <vx-date-text-edit
            v-model="fecha"
            label="Fecha*"
            outlined
            required
          />
        </v-form>

        <v-divider
          class="mb-8"
        />

        <div>
          <div
            class="graphics-header"
          >
            <div
              class="graphics-title"
            >
              <v-icon
                class="mr-2"
              >
                mdi-layers-outline
              </v-icon>
              <span
                class="text-subtitle-2 font-weight-medium"
              >Gráficos del plan</span>
            </div>

            <v-chip
              small
              class="graphics-total"
              :class="{ 'is-empty': nTotal === 0 }"
              label
            >
              Total: {{ nTotal }}
            </v-chip>
          </div>

          <v-btn
            class="graphics-btn"
            block
            outlined
            color="primary"
            @click="drawGraphicsOnMap"
          >
            <v-icon
              left
            >
              mdi-pencil-ruler
            </v-icon>
            {{ isEdit ? 'Editar gráficos' : 'Añadir gráficos al plan' }}
          </v-btn>

          <div
            class="graphics-summary mt-3"
          >
            <div
              class="summary-row"
            >
              <v-chip
                small
                label
                class="summary-chip"
              >
                <v-icon
                  left
                  small
                >
                  mdi-map-marker
                </v-icon>
                Símbolos: <span
                  class="ml-1 font-weight-medium"
                >{{ nSymbols }}</span>
              </v-chip>
              <v-chip
                small
                label
                class="summary-chip"
              >
                <v-icon
                  left
                  small
                >
                  mdi-vector-line
                </v-icon>
                Líneas: <span
                  class="ml-1 font-weight-medium"
                >{{ nPolylines }}</span>
              </v-chip>
              <v-chip
                small
                label
                class="summary-chip"
              >
                <v-icon
                  left
                  small
                >
                  mdi-vector-polygon
                </v-icon>
                Polígonos: <span
                  class="ml-1 font-weight-medium"
                >{{ nPolygons }}</span>
              </v-chip>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="isEdit"
          text
          color="red"
          @click="deletePlan"
        >
          Borrar
        </v-btn>
        <v-spacer />
        <v-btn
          text
          color="red"
          @click="closeDialog"
        >
          Cancelar
        </v-btn>
        <v-btn
          text
          color="green"
          :disabled="!isValid"
          @click="aceptar"
        >
          Aceptar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as ArcGIS from '../../../helpers/ArcGIS'
import { mapActions } from 'vuex'

export default {
  props: {
    show: Boolean,
    idIncendio: String,
    planSelected: Object,
    isEdit: Boolean
  },

  data: () => ({
    showDialog: false,

    isValid: false,

    nombre: null,
    descripcion: null,
    fecha: null,

    graphicList: []
  }),

  computed: {
    nSymbols() { // TODO: cambiar type por TIPO
      if (this.graphicList) {
        return this.graphicList.filter(x => x.TIPO !== 'polygon' && x.TIPO !== 'polyline').length
      } else return 0
    },

    nPolygons() {
      if (this.graphicList) {
        return this.graphicList.filter(x => x.TIPO === 'polygon').length
      } else return 0
    },

    nPolylines() {
      if (this.graphicList) {
        return this.graphicList.filter(x => x.TIPO === 'polyline').length
      } else return 0
    },
    nTotal() {
      return this.nSymbols + this.nPolygons + this.nPolylines
    }
  },

  watch: {
    show() {
      this.showDialog = this.show
      if (this.show) {
        if (this.isEdit) { // Editar
          this.nombre = this.planSelected.NOMBRE
          this.descripcion = this.planSelected.DESCRIPCION
          this.fecha = this.$date.formatDate(this.planSelected.FECHA, 'DD/MM/YYYY HH:mm')

          this.graphicList = [...this.planSelected.GRAFICOS] // Clonar lista de graficos
        } else {
          this.nombre = null
          this.descripcion = null
          this.graphicList = []
          this.fecha = this.$date.currentDate()
        }
      }
    }
  },

  methods: {
    ...mapActions('incendio', ['addPlanOperaciones', 'editPlanOperaciones']),

    drawGraphicsOnMap() {
      this.showDialog = false
      this.$emit('hideLeftMenu')

      this.$eventHub.$emit('showSymbology', {
        callback: this.onDrawGraphicsEnded,
        graphics: JSON.parse(JSON.stringify(this.graphicList)), // Clonar lista
        idIncendio: this.idIncendio
      })
    },

    onDrawGraphicsEnded(graphics) {
      this.showDialog = true

      this.graphicList = graphics
    },

    async aceptar() {
      if (!this.isEdit) {
        await this.addPlan()
      } else {
        await this.editPlan()
      }
    },

    async addPlan() {
      const date = this.$date.parseDate(this.fecha)
      const newPlan = {
        ID_PLAN_OPERACIONES: this.$uuid.createUUID(),
        ID_INCENDIO: this.idIncendio,
        NOMBRE: this.nombre,
        DESCRIPCION: this.descripcion,
        GRAFICOS: this.graphicList,
        FECHA: date
      }

      // Convertir datos a modelo BD
      this.modelGraphicsList(newPlan)

      try {
        await this.addPlanOperaciones(newPlan)
      } catch (error) {
        console.error('Error al crear plan de operaciones: ', error)
        this.$notify({
          title: 'Plan de operaciones',
          text: 'Error al crear plan de operaciones',
          type: 'error'
        })
      }
      this.closeDialog()
    },

    async editPlan() {
      const date = this.$date.parseDate(this.fecha)
      const editPlan = {
        ID_PLAN_OPERACIONES: this.planSelected.ID_PLAN_OPERACIONES,
        ID_INCENDIO: this.idIncendio,
        NOMBRE: this.nombre,
        DESCRIPCION: this.descripcion,
        GRAFICOS: this.graphicList,
        FECHA: date
      }

      // Convertir datos a modelo BD
      this.modelGraphicsList(editPlan)

      try {
        await this.editPlanOperaciones(editPlan)
      } catch (error) {
        console.error('Error al editar plan de operaciones: ', error)
        this.$notify({
          title: 'Plan de operaciones',
          text: 'Error al editar plan de operaciones',
          type: 'error'
        })
      }

      this.closeDialog()
    },

    modelGraphicsList(plan) {
      // Convertir datos a modelo BD
      for (let i = 0; i < plan.GRAFICOS.length; i++) {
        const g = plan.GRAFICOS[i]
        const attrs = g.attributes || g.ATRIBUTOS || {}

        g.ID_GRAFICO = attrs.ID_GRAFICO || g.ID_GRAFICO || null // g.attributes.ID_GRAFICO // TODO: attributes -> ATRIBUTOS
        g.ID_PLAN_OPERACIONES = plan.ID_PLAN_OPERACIONES

        const symbol = g.symbol || g.SIMBOLO || {}
        // FIX: Parseo de colores (solo si existen)
        if (symbol.color && typeof symbol.color.toRgba === 'function') {
          symbol.color = symbol.color.toRgba()
        }
        if (symbol.outline && symbol.outline.color && typeof symbol.outline.color.toRgba === 'function') {
          symbol.outline.color = symbol.outline.color.toRgba()
        }
        g.symbol = typeof symbol === 'string' ? JSON.parse(symbol) : symbol
        g.attributes = typeof attrs === 'string' ? JSON.parse(attrs) : attrs
        g.type = g.type || g.TIPO || 'point'
        g.geometry = g.geometry || g.GEOMETRIA || []
        if (typeof g.geometry === 'string') {
          g.geometry = JSON.parse(g.geometry)
        }
        plan.GRAFICOS[i] = g
      }

      return plan
    },

    deletePlan() {
      const msg = `Borrando plan: ${this.planSelected.NOMBRE}`

      this.$root.$confirmDialog.open('¿Está seguro?', msg).then(result => {
        if (result) {
          this.$store.dispatch('incendio/deletePlanOperaciones', this.planSelected)

          this.cerrar()
        }
      })
    },

    closeDialog() {
      this.cerrar()

      this.nombre = null
      this.descripcion = null
      this.fecha = null
      this.graphicList = []
    },

    cerrar() {
      this.$emit('close')
      ArcGIS.drawGraphicsPlanOperaciones([])
    }
  }
}
</script>

<style scoped>

.graphics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.graphics-title {
  display: flex;
  align-items: center;
}

.graphics-btn {
  text-transform: none;
  letter-spacing: 0;
}

.graphics-summary .summary-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
}
 .summary-chip {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
 }
</style>
